import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowRight, Lock, Mail, User, Briefcase, Heart, GraduationCap, Building2, Check, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Drawer as Vaul } from "vaul";
import { useIsMobile } from "../../hooks/useIsMobile";
import { redirectToCheckout as topRedirect } from "../../lib/stripeRedirect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  { id: "wedding",    label: "Casamento",  icon: Heart },
  { id: "birthday",   label: "Aniversário", icon: Sparkles },
  { id: "corporate",  label: "Corporativo", icon: Briefcase },
  { id: "graduation", label: "Formatura",   icon: GraduationCap },
  { id: "other",      label: "Outro",       icon: Building2 },
];

const PLAN_LABELS = {
  pro_monthly:                "Premium · Mensal",
  pro_yearly:                 "Premium · Anual",
  pro_monthly_personal:       "Premium · Mensal",
  pro_yearly_personal:        "Premium · Anual",
  pro_monthly_professional:   "Pro · Mensal",
  pro_yearly_professional:    "Pro · Anual",
};

const PLAN_PRICE_HINT = {
  pro_monthly: "R$ 29,90/mês",
  pro_yearly: "R$ 287/ano",
  pro_monthly_personal: "R$ 29,90/mês",
  pro_yearly_personal: "R$ 287/ano",
  pro_monthly_professional: "R$ 59,90/mês",
  pro_yearly_professional: "R$ 575/ano",
};

/**
 * GetStartedModal — Fluxo de contratação premium (não usa /login).
 *
 * Props:
 *   open: boolean
 *   onOpenChange(open)
 *   plan: string (ex: 'pro_monthly', 'pro_yearly_professional')
 *   tier: 'premium' | 'pro' (visual hint)
 *   isAuthenticated: boolean → se true, pula direto para checkout
 */
export default function GetStartedModal({ open, onOpenChange, plan = "pro_yearly", tier = "premium", isAuthenticated = false, showPlanSelection = false }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=form, 1.5=plan selection, 2=redirecting
  const [form, setForm] = useState({ name: "", email: "", password: "", event_type: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(plan);
  const [selectedTier, setSelectedTier] = useState('personal'); // 'personal' | 'professional'
  const [selectedCycle, setSelectedCycle] = useState('annual'); // 'monthly' | 'annual'
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setError(null);
    setLoading(false);
    setForm({ name: "", email: "", password: "", event_type: "" });
    setSelectedPlan(plan);
    // Infere defaults a partir do plan/tier passados
    const initialTier = (plan && plan.includes('professional')) || tier === 'pro' ? 'professional' : 'personal';
    const initialCycle = plan && plan.includes('monthly') ? 'monthly' : 'annual';
    setSelectedTier(initialTier);
    setSelectedCycle(initialCycle);
    setTimeout(() => firstFieldRef.current?.focus?.(), 240);
  }, [open, plan, tier]);

  // Se usuário já logado → comportamento depende de showPlanSelection
  useEffect(() => {
    if (!open || !isAuthenticated) return;
    if (showPlanSelection) {
      setStep(1.5);
    } else {
      setStep(2);
      redirectToCheckout(selectedPlan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAuthenticated, showPlanSelection]);

  const redirectToCheckout = async (planToUse) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/payments/checkout/session`, {
        plan: planToUse || selectedPlan,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        topRedirect(res.data.url);
        return;
      }
      throw new Error("URL de pagamento não recebida");
    } catch (err) {
      setLoading(false);
      const msg = err?.response?.data?.detail || "Não foi possível iniciar o checkout.";
      setError(msg);
      toast.error(msg);
    }
  };

  const submitSignup = async (e) => {
    e?.preventDefault?.();
    if (!form.name || !form.email || !form.password) {
      setError("Preencha nome, email e senha para continuar.");
      return;
    }
    if (form.password.length < 6) {
      setError("Senha precisa de no mínimo 6 caracteres.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const profile_type = (selectedTier === "professional") || (tier === "pro") ? "professional" : "personal";
      const res = await axios.post(`${API}/auth/quick-signup`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        event_type: form.event_type || null,
        profile_type,
      });
      const token = res.data?.access_token;
      if (token) {
        localStorage.setItem("token", token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      }
      if (showPlanSelection) {
        setLoading(false);
        setStep(1.5);
        return;
      }
      setStep(2);
      await redirectToCheckout(selectedPlan);
    } catch (err) {
      setLoading(false);
      const msg = err?.response?.data?.detail || "Não foi possível criar a conta.";
      setError(msg);
    }
  };

  const confirmPlanSelection = async () => {
    const cycleSuffix = selectedCycle === 'annual' ? 'yearly' : 'monthly';
    const tierSuffix = selectedTier === 'professional' ? 'professional' : 'personal';
    const planKey = `pro_${cycleSuffix}_${tierSuffix}`;
    setSelectedPlan(planKey);
    setStep(2);
    await redirectToCheckout(planKey);
  };

  if (!open) return null;

  const planLabel = PLAN_LABELS[plan] || "Premium · Anual";
  const priceHint = PLAN_PRICE_HINT[plan] || "";

  const FormBody = (
    <div className="space-y-5" data-testid="get-started-form">
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-white" style={{backgroundImage:'linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)'}}>
        <Sparkles size={11} className="text-white" />
        <span className="text-[10.5px] font-bold tracking-tight" style={{fontFamily:"'Nunito', sans-serif"}}>7 dias grátis · sem compromisso</span>
      </div>

      <div>
        <h2 className="text-[24px] sm:text-[28px] tracking-tight leading-tight" style={{fontFamily:"'Nunito', sans-serif", fontWeight:700, letterSpacing:'-0.02em', color:'#141023'}}>
          Comece a usar o liventy
        </h2>
        <p className="text-[14px] mt-2 leading-relaxed" style={{fontFamily:"'Nunito', sans-serif", fontWeight:500, color:'#6B7280'}}>
          Você selecionou <span style={{fontWeight:700, color:'#141023'}}>{planLabel}</span>{priceHint ? ` (${priceHint})` : ""}. Crie sua conta em 30 segundos. Você só será cobrado após o período gratuito.
        </p>
      </div>

      <form className="space-y-3" onSubmit={submitSignup}>
        <PremiumField
          icon={<User size={14} />}
          label="Nome completo"
          value={form.name}
          onChange={(v) => setForm({ ...form, name: v })}
          placeholder="Como você gostaria de ser chamado?"
          autoComplete="name"
          inputRef={firstFieldRef}
          testid="signup-name"
        />
        <PremiumField
          icon={<Mail size={14} />}
          label="Email"
          type="email"
          value={form.email}
          onChange={(v) => setForm({ ...form, email: v })}
          placeholder="seu@email.com"
          autoComplete="email"
          testid="signup-email"
        />
        <PremiumField
          icon={<Lock size={14} />}
          label="Senha"
          type="password"
          value={form.password}
          onChange={(v) => setForm({ ...form, password: v })}
          placeholder="Mínimo 6 caracteres"
          autoComplete="new-password"
          testid="signup-password"
        />

        <div>
          <label className="block text-[11px] font-medium text-zinc-500 mb-2 tracking-wide uppercase">
            Tipo de evento <span className="text-zinc-300 normal-case font-normal">· opcional</span>
          </label>
          <div className="grid grid-cols-5 gap-1.5" data-testid="event-type-grid">
            {EVENT_TYPES.map((t) => {
              const selected = form.event_type === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setForm({ ...form, event_type: selected ? "" : t.id })}
                  data-testid={`event-type-${t.id}`}
                  className={`flex flex-col items-center gap-1 px-1.5 py-2 rounded-xl transition-all ${
                    selected ? "text-white" : "bg-white text-zinc-600 hover:border-zinc-300"
                  }`}
                  style={selected
                    ? {backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 100%)', border:'1.5px solid transparent', fontFamily:"'Nunito', sans-serif"}
                    : {border:'1.5px solid #E9E5FF', fontFamily:"'Nunito', sans-serif"}}
                >
                  <t.icon size={15} strokeWidth={1.7} />
                  <span className="text-[9.5px] font-medium tracking-tight">{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-[12px] text-red-700 flex items-start gap-2" data-testid="signup-error">
            <X size={13} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="signup-submit"
          className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full text-white text-[14px] font-bold tracking-tight active:scale-[0.98] transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            fontFamily:"'Nunito', sans-serif",
            backgroundImage:'linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)',
            boxShadow:'0 10px 28px -8px rgba(107,61,246,0.50), 0 4px 14px -6px rgba(255,77,141,0.30)',
          }}
        >
          {loading ? (
            <>
              <Loader2 size={15} className="animate-spin" />
              Criando sua conta…
            </>
          ) : (
            <>
              Continuar para o checkout
              <ArrowRight size={15} />
            </>
          )}
        </button>
      </form>

      <div className="flex items-center justify-center gap-4 text-[10.5px] text-zinc-400 pt-1">
        <span className="inline-flex items-center gap-1"><Lock size={10} /> Checkout criptografado</span>
        <span className="inline-flex items-center gap-1"><Check size={10} /> Cancele quando quiser</span>
      </div>
      <p className="text-center text-[10.5px] text-zinc-400">
        Ao criar sua conta você concorda com os{" "}
        <button type="button" onClick={() => navigate("/terms")} className="underline hover:text-zinc-600">Termos</button> e a{" "}
        <button type="button" onClick={() => navigate("/privacy")} className="underline hover:text-zinc-600">Privacidade</button>.
        Já tem conta?{" "}
        <button type="button" onClick={() => { onOpenChange?.(false); navigate("/login"); }} className="underline font-medium text-zinc-700 hover:text-zinc-900" data-testid="signup-go-to-login">Entrar</button>.
      </p>
    </div>
  );

  const RedirectBody = (
    <div className="py-10 text-center space-y-4" data-testid="get-started-redirecting">
      <div className="w-12 h-12 mx-auto rounded-full bg-zinc-100 flex items-center justify-center">
        <Loader2 className="animate-spin text-zinc-600" size={22} />
      </div>
      <div>
        <p className="text-[15px] font-semibold text-zinc-900 tracking-tight">Indo para o checkout seguro</p>
        <p className="text-[12.5px] text-zinc-500 mt-1">Estamos finalizando sua conta e abrindo o Stripe…</p>
      </div>
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-[12px] text-red-700">
          {error}
          <button onClick={() => redirectToCheckout(selectedPlan)} className="ml-2 underline font-medium">Tentar novamente</button>
        </div>
      )}
    </div>
  );

  // ───────── PLAN SELECTION BODY ─────────
  const planTable = {
    personal: {
      monthly: { price: 29.90,  name: 'Premium', monthly_equiv: null },
      annual:  { price: 287.00, name: 'Premium Anual', monthly_equiv: 23.92 },
    },
    professional: {
      monthly: { price: 59.90,  name: 'Pro', monthly_equiv: null },
      annual:  { price: 575.00, name: 'Pro Anual', monthly_equiv: 47.92 },
    },
  };
  const currentTierPlan = planTable[selectedTier === 'professional' ? 'professional' : 'personal'][selectedCycle];

  const PlanBody = (
    <div className="space-y-5 pt-2" data-testid="get-started-plan-selection">
      <div className="text-center">
        <h3 className="text-[20px] font-semibold tracking-tight text-zinc-900" style={{fontFamily:"'Inter', sans-serif"}}>Conta criada! Escolha seu plano</h3>
        <p className="text-[12.5px] text-zinc-500 mt-1">7 dias grátis · cancele quando quiser</p>
      </div>

      {/* Tier */}
      <div>
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-zinc-400 mb-2">Tipo de uso</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setSelectedTier('personal')}
            className={`px-3 py-3 rounded-xl text-[12.5px] font-medium transition-all ${
              selectedTier === 'personal' ? 'bg-zinc-900 text-white shadow-md' : 'bg-zinc-50 text-zinc-700 hover:bg-zinc-100'
            }`}
            data-testid="modal-tier-personal"
          >
            <span className="block">Pessoal</span>
            <span className={`block text-[10.5px] mt-0.5 font-normal ${selectedTier === 'personal' ? 'text-white/70' : 'text-zinc-500'}`}>1 evento + recursos completos</span>
          </button>
          <button
            onClick={() => setSelectedTier('professional')}
            className={`px-3 py-3 rounded-xl text-[12.5px] font-medium transition-all ${
              selectedTier === 'professional' ? 'bg-zinc-900 text-white shadow-md' : 'bg-zinc-50 text-zinc-700 hover:bg-zinc-100'
            }`}
            data-testid="modal-tier-professional"
          >
            <span className="block">Profissional</span>
            <span className={`block text-[10.5px] mt-0.5 font-normal ${selectedTier === 'professional' ? 'text-white/70' : 'text-zinc-500'}`}>Multi-eventos + workspace</span>
          </button>
        </div>
      </div>

      {/* Cycle */}
      <div>
        <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-zinc-400 mb-2">Ciclo de cobrança</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setSelectedCycle('monthly')}
            className={`px-3 py-3 rounded-xl text-[12.5px] font-medium transition-all ${
              selectedCycle === 'monthly' ? 'bg-zinc-900 text-white shadow-md' : 'bg-zinc-50 text-zinc-700 hover:bg-zinc-100'
            }`}
            data-testid="modal-cycle-monthly"
          >
            Mensal
          </button>
          <button
            onClick={() => setSelectedCycle('annual')}
            className={`relative px-3 py-3 rounded-xl text-[12.5px] font-medium transition-all ${
              selectedCycle === 'annual' ? 'text-white shadow-md' : 'bg-zinc-50 text-zinc-700 hover:bg-zinc-100'
            }`}
            style={selectedCycle === 'annual' ? {backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 100%)'} : {}}
            data-testid="modal-cycle-annual"
          >
            Anual
            <span className={`absolute -top-2 -right-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
              selectedCycle === 'annual' ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-700'
            }`}>−20%</span>
          </button>
        </div>
      </div>

      {/* Resumo */}
      <div className="rounded-2xl border border-zinc-200/70 p-4 bg-gradient-to-br from-violet-50/40 via-white to-orange-50/30">
        <div className="flex items-start justify-between gap-2 mb-2">
          <p className="text-[14.5px] font-semibold tracking-tight text-zinc-900">{currentTierPlan.name}</p>
          {selectedCycle === 'annual' && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide bg-emerald-100 text-emerald-700">20% OFF</span>
          )}
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-[26px] font-semibold tracking-tight text-zinc-900">
            R$ {currentTierPlan.price.toFixed(2).replace('.', ',')}
          </span>
          <span className="text-[12px] text-zinc-500">/ {selectedCycle === 'annual' ? 'ano' : 'mês'}</span>
        </div>
        {selectedCycle === 'annual' && currentTierPlan.monthly_equiv && (
          <p className="text-[11px] text-zinc-500 mt-0.5">Equivale a R$ {currentTierPlan.monthly_equiv.toFixed(2).replace('.', ',')}/mês</p>
        )}
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-[12px] text-red-700">{error}</div>
      )}

      <button
        type="button"
        onClick={confirmPlanSelection}
        disabled={loading}
        className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full text-white text-[14px] font-bold tracking-tight active:scale-[0.98] transition-transform disabled:opacity-60"
        style={{
          fontFamily:"'Inter', sans-serif",
          backgroundImage:'linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)',
          boxShadow:'0 10px 28px -8px rgba(107,61,246,0.50)',
        }}
        data-testid="modal-confirm-plan"
      >
        {loading ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
        Iniciar trial de 7 dias
      </button>

      <p className="text-center text-[10.5px] text-zinc-400">
        Sua assinatura inicia após os 7 dias gratuitos · cancele a qualquer momento.
      </p>
    </div>
  );

  if (isMobile) {
    return (
      <Vaul.Root open={open} onOpenChange={(o) => { if (!o && !loading) onOpenChange?.(false); }}>
        <Vaul.Portal>
          <Vaul.Overlay className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-[2px]" />
          <Vaul.Content className="fixed inset-x-0 bottom-0 z-[81] flex flex-col bg-white rounded-t-3xl outline-none max-h-[94vh]" data-testid="get-started-modal-mobile">
            <div className="mx-auto mt-2.5 mb-1 h-1.5 w-12 rounded-full bg-zinc-200 shrink-0" />
            <div className="flex items-center justify-end px-5 pt-1 pb-1">
              <button onClick={() => !loading && onOpenChange?.(false)} className="p-1.5 -mr-1.5 rounded-lg active:bg-zinc-100 transition-colors" data-testid="get-started-close">
                <X size={18} className="text-zinc-500" />
              </button>
            </div>
            <Vaul.Title className="sr-only">Comece a usar o liventy</Vaul.Title>
            <Vaul.Description className="sr-only">Crie sua conta em segundos e ative o trial de 7 dias</Vaul.Description>
            <div className="px-5 pb-[max(env(safe-area-inset-bottom),20px)] overflow-y-auto">
              {step === 1 ? FormBody : step === 1.5 ? PlanBody : RedirectBody}
            </div>
          </Vaul.Content>
        </Vaul.Portal>
      </Vaul.Root>
    );
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={() => !loading && onOpenChange?.(false)} />
      <div className="relative bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(24,24,27,0.4)] max-w-md w-full max-h-[92vh] overflow-y-auto" data-testid="get-started-modal-desktop">
        <button onClick={() => !loading && onOpenChange?.(false)} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-zinc-100 transition-colors z-10" data-testid="get-started-close">
          <X size={18} className="text-zinc-500" />
        </button>
        <div className="p-7">
          {step === 1 ? FormBody : step === 1.5 ? PlanBody : RedirectBody}
        </div>
      </div>
    </div>
  );
}

function PremiumField({ icon, label, value, onChange, type = "text", placeholder, autoComplete, inputRef, testid }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-zinc-500 mb-1.5 tracking-wide uppercase">{label}</label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">{icon}</span>
        <input
          ref={inputRef}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          data-testid={testid}
          className="w-full h-11 pl-9 pr-3 text-[14px] rounded-xl border border-zinc-200 bg-white text-zinc-900 placeholder:text-zinc-300 focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/5 transition-all"
        />
      </div>
    </div>
  );
}
